import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Row,
  Col,
  Pagination,
  Dropdown,
  Spinner,
  Card,
} from "react-bootstrap";
import { FaSearch, FaTimes, FaFilter, FaEye } from "react-icons/fa";
import { diffWords } from "diff";
import { useNavigate } from "react-router-dom";
import { showMyAlert } from "../../Utils/alertService.js";

const AuditTrailLogs = () => {
  const [auditData, setAuditData] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [componentNames, setComponentNames] = useState([]);
  const [actionNames, setActionNames] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [componentFilter, setComponentFilter] = useState("All");
  const [actionFilter, setActionFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const navigate = useNavigate();

  // ---------- Auth Check (optional but consistent) ----------
  useEffect(() => {
    try {
      const userMobile = sessionStorage.getItem("userMobile");
      const userRole = sessionStorage.getItem("role");

      if (!userMobile || userMobile.trim() === "") {
        navigate("/signIn", { replace: true });
        return;
      }

      // Only superadmin can view audit trail (adjust if needed)
      if (userRole !== "superadmin") {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "No Access",
          message: "You are not authorized to access this page.",
          duration: 2500, // auto-close in 2.5s
        });
        navigate(-1, { replace: true });
      }
    } catch (err) {
      navigate(-1, { replace: true });
    }
  }, [navigate]);

  // ---------- Fetch Audit Logs ----------
  const fetchAuditData = async () => {
    try {
      setLoading(true);
      const sessionCompanyKey = sessionStorage.getItem("companyKey");
      const res = await fetch(
        "https://api.dremerz.net/api/DZ_8925_PayrollProduct_History_AuditLog_AllLogs"
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch audit logs: ${res.status}`);
      }

      const data = await res.json();

      // Ensure it's an array + latest first
      const normalized = Array.isArray(data) ? data.slice().reverse() : [];
      const filteredData = normalized.filter(
        (data) => data.companyKey === sessionCompanyKey
      );
      setAuditData(filteredData);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      setAuditData([]);

      // Use your custom alert if available
      showMyAlert?.({
        type: "danger",
        title: "Error",
        message: "Failed to load audit trail logs.",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditData();
  }, []);

  // ---------- Build Component / Action / Role Lists ----------
  useEffect(() => {
    const uniqueComponents = [
      "All",
      ...new Set(auditData.map((item) => item.componentName).filter(Boolean)),
    ];

    const uniqueActions = [
      "All",
      ...new Set(auditData.map((item) => item.action).filter(Boolean)),
    ];

    const uniqueRoles = [
      "All",
      ...new Set(
        auditData
          .map((item) => item.updateByUserRole)
          .filter((role) => role && role.trim() !== "")
      ),
    ];

    setRoleNames(uniqueRoles);
    setComponentNames(uniqueComponents);
    setActionNames(uniqueActions);

    // Save to state via closures (but we only render from filters)
    if (!uniqueActions.includes(actionFilter)) setActionFilter("All");
    if (!uniqueRoles.includes(roleFilter)) setRoleFilter("All");
  }, [auditData]);

  // ---------- Search & Filter ----------
  useEffect(() => {
    let result = [...auditData];

    // Component filter
    if (componentFilter !== "All") {
      result = result.filter((item) => item.componentName === componentFilter);
    }

    // Action filter
    if (actionFilter !== "All") {
      result = result.filter((item) => item.action === actionFilter);
    }

    // Role filter
    if (roleFilter !== "All") {
      result = result.filter((item) => item.updateByUserRole === roleFilter);
    }

    // Search across multiple fields
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((item) => {
        const fields = [
          item.id,
          item.action,
          item.componentName,
          item.updateByUserRole,
          item.updateByUserEmail,
          item.updateByUserFullName,
          item.updateByUserId,
          item.updateByUserMobile,
          item.updateByUserName,
          item.updateDate,
          item.updatedTime,
        ];

        return fields
          .filter((f) => f !== undefined && f !== null)
          .some((f) => String(f).toLowerCase().includes(term));
      });
    }

    setFilteredLogs(result);
    setCurrentPage(1);
  }, [auditData, searchTerm, componentFilter, actionFilter, roleFilter]);

  // ---------- Pagination ----------
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  const clearSearch = () => setSearchTerm("");

  const clearFilters = () => {
    setComponentFilter("All");
    setActionFilter("All");
    setRoleFilter("All");
    setSearchTerm("");
  };

  // ---------- Helpers: Parse & Render Data ----------
  const parseData = (data, depth = 0) => {
    if (!data || typeof data !== "string" || depth >= 3) return data;

    try {
      const parsed = JSON.parse(data);

      if (typeof parsed === "object" && parsed !== null) {
        for (const key in parsed) {
          if (typeof parsed[key] === "string") {
            parsed[key] = parseData(parsed[key], depth + 1);
          }
        }
      }

      return parseData(parsed, depth + 1);
    } catch {
      return data;
    }
  };

  const formatData = (data) => {
    if (data === null || data === undefined) return "null";
    if (typeof data === "object") return JSON.stringify(data, null, 2);
    return String(data);
  };

  const renderObject = (obj) => {
    if (!obj || typeof obj !== "object") {
      return <div className="text-muted">No data</div>;
    }

    return (
      <table className="table table-sm table-borderless mb-0">
        <tbody>
          {Object.entries(obj).map(([key, value]) => (
            <tr key={key}>
              <td className="text-muted" style={{ width: "30%" }}>
                {key}:
              </td>
              <td>
                {Array.isArray(value) ? (
                  <ul className="list-unstyled mb-0">
                    {value.map((item, index) => (
                      <li key={index}>
                        {typeof item === "object"
                          ? renderObject(item)
                          : String(item)}
                      </li>
                    ))}
                  </ul>
                ) : typeof value === "object" ? (
                  renderObject(value)
                ) : (
                  String(value)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderParsedData = (data) => {
    if (!data) return <div className="text-muted">No data</div>;

    const parsed = parseData(data);

    if (Array.isArray(parsed)) {
      return (
        <ul className="list-unstyled mb-0">
          {parsed.map((item, index) => (
            <li key={index}>
              {typeof item === "object" ? renderObject(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof parsed === "object") return renderObject(parsed);

    return <div>{String(parsed)}</div>;
  };

  const renderDiff = (oldData, newData) => {
    const oldParsed = parseData(oldData);
    const newParsed = parseData(newData);

    // Object-level diff (field-wise)
    if (
      oldParsed &&
      newParsed &&
      typeof oldParsed === "object" &&
      typeof newParsed === "object"
    ) {
      const allKeys = new Set([
        ...Object.keys(oldParsed || {}),
        ...Object.keys(newParsed || {}),
      ]);

      return (
        <table className="table table-sm table-borderless mb-0">
          <tbody>
            {Array.from(allKeys).map((key) => {
              const oldVal = oldParsed?.[key];
              const newVal = newParsed?.[key];
              const oldStr = formatData(oldVal);
              const newStr = formatData(newVal);

              if (oldStr === newStr) {
                return (
                  <tr key={key}>
                    <td className="text-muted" style={{ width: "30%" }}>
                      {key}:
                    </td>
                    <td>{renderParsedData(oldVal)}</td>
                  </tr>
                );
              }

              const diff = diffWords(oldStr, newStr);

              return (
                <tr key={key}>
                  <td className="text-muted" style={{ width: "30%" }}>
                    {key}:
                  </td>
                  <td>
                    <div>
                      <small className="text-danger">Old: </small>
                      {diff.map(
                        (part, i) =>
                          part.removed && (
                            <span
                              key={`old-${i}`}
                              style={{ backgroundColor: "lavenderblush" }}
                            >
                              {part.value}
                            </span>
                          )
                      )}
                    </div>
                    <div>
                      <small className="text-success">New: </small>
                      {diff.map(
                        (part, i) =>
                          part.added && (
                            <span
                              key={`new-${i}`}
                              style={{ backgroundColor: "honeydew" }}
                            >
                              {part.value}
                            </span>
                          )
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }
  
    // Primitive / fallback inline diff
    const textOld = formatData(oldParsed);
    const differences = diffWords(textOld, textNew);
    const textNew = formatData(newParsed);
   

    return (
      <div style={{ whiteSpace: "pre-wrap" }}>
        {differences.map((part, index) => (
          <span
            key={index}
            style={{
              backgroundColor: part.added
                ? "honeydew"
                : part.removed
                ? "lavenderblush"
                : "transparent",
            }}
          >
            {part.value}
          </span>
        ))}
      </div>
    );
  };

  // ---------- Modal ----------
  const handleShowModal = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
    setShowModal(false);
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading audit logs...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header Section (UserAuthentication style) */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2">
        <div className="col-12 col-md-6">
          <h5 className="mb-0 d-flex align-items-center">Audit Trail Logs</h5>
        </div>
      </div>

      {/* Search & Filters Row */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2">
        {/* Search */}
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={clearSearch}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="col-12 col-md-8">
          <div className="d-flex flex-wrap justify-content-center justify-content-md-end gap-2">
            {/* Component Filter */}
            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="component-filter"
              >
                <FaFilter className="me-2" />
                {componentFilter === "All" ? "All Components" : componentFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setComponentFilter("All")}>
                  All Components
                </Dropdown.Item>
                {componentNames
                  .filter((name) => name !== "All")
                  .map((name) => (
                    <Dropdown.Item
                      key={name}
                      onClick={() => setComponentFilter(name)}
                    >
                      {name}
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Action Filter */}
            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="action-filter"
              >
                <FaFilter className="me-2" />
                {actionFilter === "All" ? "All Actions" : actionFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setActionFilter("All")}>
                  All Actions
                </Dropdown.Item>
                {actionNames
                  .filter((name) => name !== "All")
                  .map((name) => (
                    <Dropdown.Item
                      key={name}
                      onClick={() => setActionFilter(name)}
                    >
                      {name}
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Role Filter */}
            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="role-filter"
              >
                <FaFilter className="me-2" />
                {roleFilter === "All" ? "All Roles" : roleFilter}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setRoleFilter("All")}>
                  All Roles
                </Dropdown.Item>
                {roleNames
                  .filter((name) => name !== "All")
                  .map((name) => (
                    <Dropdown.Item
                      key={name}
                      onClick={() => setRoleFilter(name)}
                    >
                      {name}
                    </Dropdown.Item>
                  ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Clear */}
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={clearFilters}
              disabled={
                componentFilter === "All" &&
                actionFilter === "All" &&
                roleFilter === "All" &&
                !searchTerm
              }
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="d-none d-md-block">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th id="text-white-important">ID</th>
              <th id="text-white-important">Component</th>
              <th id="text-white-important">Action</th>
              <th id="text-white-important">User</th>
              <th id="text-white-important">Role</th>
              <th id="text-white-important">Mobile</th>
              <th id="text-white-important">Date</th>
              <th id="text-white-important">Time</th>
              <th id="text-white-important">View</th>
            </tr>
          </thead>
          <tbody>
            {currentLogs.length > 0 ? (
              currentLogs.map((log, index) => (
                <tr key={index}>
                  <td>{filteredLogs.length - index}</td>
                  <td>{log.componentName}</td>
                  <td>{log.action}</td>
                  <td>
                    {log.updateByUserFullName || log.updateByUserName || "-"}
                  </td>
                  <td>{log.updateByUserRole || "-"}</td>
                  <td>{log.updateByUserMobile || "-"}</td>
                  <td>{log.updateDate || "-"}</td>
                  <td>{log.updatedTime || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-info"
                      onClick={() => handleShowModal(log)}
                    >
                      {/* <FaEye className="me-1" /> */}
                      Details
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center text-muted py-4">
                  {searchTerm ||
                  componentFilter !== "All" ||
                  actionFilter !== "All" ||
                  roleFilter !== "All"
                    ? "No logs match the current filters."
                    : "No audit logs available."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="d-block d-md-none">
        {currentLogs.length > 0 ? (
          currentLogs.map((log, index) => (
            <Card key={index} className="mb-3 shadow-sm border">
              <Card.Body className="p-2">
                <Table bordered size="sm" className="mb-2">
                  <tbody>
                    <tr>
                      <td className="fw-bold">ID</td>
                      <td>{filteredLogs.length - index}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Component</td>
                      <td>{log.componentName}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Action</td>
                      <td>{log.action}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">User</td>
                      <td>
                        {log.updateByUserFullName ||
                          log.updateByUserName ||
                          "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Role</td>
                      <td>{log.updateByUserRole || "-"}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Mobile</td>
                      <td>{log.updateByUserMobile || "-"}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Date</td>
                      <td>{log.updateDate || "-"}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Time</td>
                      <td>{log.updatedTime || "-"}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">View</td>
                      <td>
                        <Button
                          size="sm"
                          variant="info"
                          className="w-100"
                          onClick={() => handleShowModal(log)}
                        >
                          <FaEye className="me-1" />
                          Details
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ))
        ) : (
          <div className="text-center text-muted py-3">No audit logs found</div>
        )}
      </div>

      {/* Results Count */}
      {filteredLogs.length > 0 && (
        <div className="col-12 text-end mt-1">
          <span className="text-muted small">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredLogs.length)} of{" "}
            {filteredLogs.length} logs
          </span>
        </div>
      )}

      {/* Pagination Controls (top) */}
      {filteredLogs.length > 0 && (
        <div className="row align-items-center justify-content-between p-2">
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="items-per-page-dropdown"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                )
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Audit Log Details {selectedLog?.id && `- ID: ${selectedLog.id}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <>
              <h5>Basic Information</h5>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold">Component</td>
                    <td>{selectedLog.componentName}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Action</td>
                    <td>{selectedLog.action}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Date</td>
                    <td>{selectedLog.updateDate || "-"}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Time</td>
                    <td>{selectedLog.updatedTime || "-"}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Updated By</td>
                    <td>
                      {selectedLog.updateByUserFullName ||
                        selectedLog.updateByUserName ||
                        "-"}{" "}
                      {selectedLog.updateByUserRole &&
                        `(${selectedLog.updateByUserRole})`}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Mobile</td>
                    <td>{selectedLog.updateByUserMobile || "-"}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Email</td>
                    <td>{selectedLog.updateByUserEmail || "-"}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">User ID</td>
                    <td>{selectedLog.updateByUserId || "-"}</td>
                  </tr>
                </tbody>
              </Table>

              <h5 className="mt-4">Data Changes</h5>
              <div className="p-3 bg-light rounded">
                <h6>Old Data</h6>
                <div className="p-2 bg-white rounded mb-2">
                  {renderParsedData(selectedLog.oldData)}
                </div>

                <h6>New Data</h6>
                <div className="p-2 bg-white rounded mb-2">
                  {renderParsedData(selectedLog.newData)}
                </div>

                <h6>Differences</h6>
                <div className="p-2 bg-white rounded">
                  {renderDiff(selectedLog.oldData, selectedLog.newData)}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AuditTrailLogs;
